import React from 'react';
import { connect } from 'react-redux';
import { TextField, Button } from '@material-ui/core';

import { setData } from '../../../redux/dataSlice';
import { baseApiUrl, getTenantConfig } from '../../../config';
import customClientConfig from '../../../customClientConfig';

const { tenant } = getTenantConfig();

// Axsome supports two entry paths:
//   1. Standard invite code in the URL -> user must type the meetingId:contactId
//      lookup code on this screen (matches the historic flow).
//   2. A meetingId:contactId URL that matches `additionalInviteCodeFormat` ->
//      we already have the lookup code from the URL and can skip the manual
//      code entry step entirely.
const additionalFormatPattern = customClientConfig[tenant]?.additionalInviteCodeFormat;
const urlMatchesAdditionalFormat = (urlInviteCode) => {
  if (!additionalFormatPattern || !urlInviteCode) return false;
  return new RegExp(additionalFormatPattern).test(urlInviteCode);
};

const AxsomeNpiLookupComponent = (props) => {
  const urlInviteCode = props.match?.params?.invitecode || '';
  const isPrefilledCode = urlMatchesAdditionalFormat(urlInviteCode);

  const [registrantType, setRegistrantType] = React.useState(null); // 'hcp' or 'apr'
  const [code, setCode] = React.useState(isPrefilledCode ? urlInviteCode : '');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Hits the contact-lookup endpoint with the user's lookup code and the
  // meeting's invitationcode. On success, pushes the contact data into Redux
  // which causes the parent RegForm to swap in the registration form.
  const runContactLookup = async (lookupCode, regType) => {
    const invitecode = props.meetingInfo?.invitationcode;
    if (!invitecode) {
      setError('Meeting information not available. Please try again.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${baseApiUrl}/api/contact-lookup?code=${encodeURIComponent(lookupCode)}&invitecode=${invitecode}`
      );
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        setLoading(false);
        return;
      }

      props.setData({
        isLicensed: data.is_prescriber,
        npiInfo: data,
        registrantType: regType
      });
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  // Manual-entry path: require `:` separator before hitting the backend so
  // we fail fast on obviously malformed codes.
  const lookupContact = () => {
    if (!code || !code.includes(':')) {
      setError('Please enter a valid code.');
      return;
    }
    runContactLookup(code, registrantType);
  };

  // Called when the user picks HCP or Authorized Representative.
  // When the URL already carries a meetingId:contactId code we can run the
  // lookup immediately with the type the user just selected (avoiding the
  // stale-closure problem of setState + separate effect).
  const handleRegistrantType = (regType) => {
    setRegistrantType(regType);
    if (isPrefilledCode) {
      runContactLookup(urlInviteCode, regType);
    }
  };

  // Step 1: registrant-type selection is always shown, regardless of which
  // invite-code path brought us here.
  if (!registrantType) {
    return (
      <div style={{ marginTop: 50 }}>
        <p style={{ fontSize: 16, marginBottom: 30 }}>
          Are you an HCP registering for yourself or an Authorized Representative registering on
          behalf of an HCP?
        </p>
        <div style={{ display: 'flex', gap: 15 }}>
          <Button
            variant="contained"
            className="hlx-button"
            style={{ minWidth: 200 }}
            onClick={() => handleRegistrantType('apr')}
          >
            Authorized Representative
          </Button>
          <Button
            variant="contained"
            className="hlx-button"
            style={{ minWidth: 200 }}
            onClick={() => handleRegistrantType('hcp')}
          >
            HCP
          </Button>
        </div>
        {error && (
          <p style={{ color: 'red', marginTop: 20 }}>{error}</p>
        )}
      </div>
    );
  }

  // Prefilled path: the lookup is already running (or retrying on error).
  // Show a spinner/error but no manual entry field.
  if (isPrefilledCode) {
    return (
      <div style={{ marginTop: 50 }}>
        <p style={{ fontSize: 16 }}>
          {loading ? 'Validating your invitation...' : (error || 'Processing...')}
        </p>
        {error && (
          <div style={{ display: 'flex', gap: 5, marginTop: 10 }}>
            <Button
              variant="contained"
              className="hlx-button"
              style={{ width: 120 }}
              onClick={() => runContactLookup(urlInviteCode, registrantType)}
              disabled={loading}
            >
              Retry
            </Button>
            <Button
              className="cancel-btn hlx-button"
              variant="contained"
              style={{ width: 120 }}
              onClick={() => {
                setRegistrantType(null);
                setError(null);
              }}
            >
              Back
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Step 2 (manual path): standard invite code was used, so prompt for the
  // meetingId:contactId code printed on the invitation.
  return (
    <div style={{ marginTop: 50 }}>
      <p style={{ fontSize: 16, marginBottom: 10 }}>Enter Code Listed on Invitation</p>
      <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 600 }}>
        <TextField
          value={code}
          onChange={(event) => setCode(event.target.value)}
          margin={'dense'}
          variant={'outlined'}
          inputProps={{
            style: { padding: '8px 12px' }
          }}
          error={Boolean(error)}
          helperText={error}
        />
        <div style={{ display: 'flex', gap: 5, marginTop: 10 }}>
          <Button
            variant="contained"
            className="hlx-button"
            style={{ width: 120 }}
            onClick={lookupContact}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Search'}
          </Button>
          <Button
            className="cancel-btn hlx-button"
            variant="contained"
            style={{ width: 120 }}
            onClick={() => {
              setRegistrantType(null);
              setCode('');
              setError(null);
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ data }) => {
  const { meetingInfo } = data;
  return { meetingInfo };
};

export default connect(mapStateToProps, { setData })(AxsomeNpiLookupComponent);
